import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { getLenisInstance } from '../core/scroll-manager.js';

// SVG Constants (URL Encoded for Data URI)
const DEFAULT_MARKER_SVG = `data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28"><circle cx="14.14" cy="13.99" r="13.5" style="fill: %23424142;"/><path d="M14.14,27.49C6.7,27.49.64,21.43.64,13.99S6.7.49,14.14.49s13.5,6.06,13.5,13.5-6.06,13.5-13.5,13.5ZM14.14,3.49c-5.79,0-10.5,4.71-10.5,10.5s4.71,10.5,10.5,10.5,10.5-4.71,10.5-10.5S19.93,3.49,14.14,3.49Z" style="fill: %23fff;"/></svg>`;

const SELECTED_MARKER_SVG = `data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 46 46"><path d="M35.72,5.48C32.1,2.15,27.91.48,23.14.48s-8.95,1.66-12.55,4.99c-3.62,3.35-5.45,7.83-5.45,13.46,0,3.76,1.51,7.83,4.48,12.24,2.99,4.41,7.49,9.18,13.52,14.31,6.05-5.13,10.55-9.9,13.55-14.31,2.97-4.41,4.45-8.48,4.45-12.24,0-5.63-1.8-10.1-5.42-13.46ZM26.34,21.66c-.9.88-1.96,1.33-3.19,1.33s-2.3-.45-3.17-1.33c-.88-.88-1.33-1.94-1.33-3.17s.45-2.3,1.33-3.17c.88-.88,1.94-1.33,3.17-1.33s2.3.45,3.19,1.33c.88.88,1.31,1.94,1.31,3.17s-.43,2.3-1.31,3.17Z" style="fill: %2396cd83;"/><path d="M23.14,45.49l-.98-.85c-5.14-4.46-9.16-8.71-11.96-12.63l-.57-.82c-2.98-4.43-4.49-8.55-4.49-12.25,0-5.58,1.83-10.11,5.44-13.46C14.17,2.16,18.4.48,23.14.48s8.71,1.58,12.24,4.69l.33.3c3.61,3.34,5.43,7.87,5.43,13.46,0,3.73-1.5,7.85-4.45,12.23-2.83,4.18-7.06,8.71-12.57,13.47l-.98.85ZM23.14,3.48c-4.02,0-7.46,1.37-10.52,4.2-3.02,2.79-4.48,6.47-4.48,11.25,0,3.1,1.33,6.65,3.96,10.56l.55.78c2.47,3.47,6,7.24,10.49,11.23,4.83-4.27,8.54-8.31,11.06-12.02,2.61-3.88,3.94-7.43,3.94-10.56,0-4.79-1.46-8.47-4.46-11.25l-.3-.27c-2.99-2.64-6.35-3.93-10.24-3.93ZM23.14,25.98c-2.02,0-3.85-.76-5.29-2.21-1.44-1.44-2.21-3.27-2.21-5.29s.76-3.85,2.21-5.29c1.44-1.44,3.27-2.21,5.29-2.21,1.87,0,3.61.67,5.02,1.93l.25.23c1.48,1.48,2.23,3.31,2.23,5.34s-.75,3.87-2.18,5.29l-.09.08c-1.4,1.37-3.23,2.12-5.23,2.12ZM23.14,13.98c-1.22,0-2.29.45-3.17,1.33-.88.88-1.33,1.95-1.33,3.17s.45,2.29,1.33,3.17c.88.88,1.95,1.33,3.17,1.33s2.29-.45,3.2-1.33l.02-.02c.85-.87,1.28-1.92,1.28-3.15s-.44-2.31-1.3-3.17h0s-.2-.18-.2-.18c-.84-.75-1.86-1.14-3-1.14Z" style="fill: %23fff;"/></svg>`;

let map;
let markerCluster;
let markers = [];
let infoWindow;
let mapInitialized = false;
let selectedMarker = null;

/**
 * Checks if the current user agent is likely a bot or crawler.
 * @returns {boolean} True if the user agent matches a bot pattern, false otherwise.
 */
function isBot() {
    const botPatterns = [
        /bot/i, /crawler/i, /spider/i, /crawling/i, /slurp/i, /seek/i,
        /google/i, /bing/i, /yandex/i, /duckduck/i, /msn/i,
        /baidu/i, /sogou/i, /exabot/i, /facebot/i, /petal/i,
        /ia_archiver/i, /archive\.org/i, /semrush/i, /ahrefs/i, /mj12/i,
        /dot/i, /nutch/i, /seznam/i, /grapeshot/i, /linkpad/i,
        /megaindex/i, /blex/i, /bytespider/i, /zoominfo/i, /voila/i,
        /psbot/i, /censysinspect/i, /serpstat/i, /barkrowler/i,
        /httpunit/i, /python-urllib/i, /libwww/i, /commons-httpclient/i,
        /java/i, /wget/i, /curl/i, /uptimerobot/i
    ];
    return botPatterns.some(pattern => pattern.test(navigator.userAgent));
}

/**
 * Initializes the Properties Map if the container exists.
 */
export function initializePropertiesMap() {
    const mapEl = document.getElementById('properties-map');
    if (!mapEl) return;

    // Reset state
    mapInitialized = false;
    map = null;
    markers = [];
    markerCluster = null;
    selectedMarker = null;

    setupEventListeners();

    // Check for querystring to auto-load map
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('map-display') === '1') {
        const toggle = document.getElementById('map-view-toggle');
        if (toggle) {
            toggle.checked = true;
            // Trigger change event to handle UI and loading
            toggle.dispatchEvent(new Event('change'));
        }
    }
}

function loadAndInitMap(mapEl) {
    if (mapInitialized) return;

    // Prevent bots from loading the Google Maps API to save on costs.
    if (isBot()) {
        console.warn('Properties Map: Bot detected. Skipping map initialization.');
        return;
    }

    // If Google Maps is already loaded (e.g. by another component), use it directly.
    if (window.google && window.google.maps) {
        initMap(mapEl);
        return;
    }

    const apiKey = window.app?.settings?.global_google_maps_api_key;
    if (!apiKey) {
        console.warn('Properties Map: No API Key found in window.app.settings.');
        return;
    }

    const loader = new Loader({
        apiKey: apiKey,
        version: "weekly",
        libraries: ["places", "marker"]
    });

    loader.load().then(() => {
        initMap(mapEl);
    }).catch(e => {
        console.error('Properties Map: Failed to load Google Maps API', e);
    });
}

/**
 * Destroys the map instance and cleans up event listeners.
 */
export function destroyPropertiesMap() {
    // Remove the ALM callback event listener
    window.removeEventListener('alm:complete', onAlmComplete);
    if (markerCluster) {
        markerCluster.clearMarkers();
        markerCluster = null;
    }
    map = null;
    markers = [];
    selectedMarker = null;
    mapInitialized = false;
}

function initMap(mapEl) {
    if (mapInitialized) return;

    let styles = [];
    if (window.app?.settings?.googleMapsDefaultSettings?.stylesString) {
        try {
            styles = JSON.parse(window.app.settings.googleMapsDefaultSettings.stylesString);
        } catch(e) {}
    }

    map = new google.maps.Map(mapEl, {
        zoom: 12,
        maxZoom: 17,
        center: { lat: -32.9283, lng: 151.7817 }, // Default center
        styles: styles,
        mapTypeControl: false,
        streetViewControl: false,
        rotateControl: false,
        zoomControl: true,
        zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_CENTER
        },
        tilt: 0
    });

    /**
     * Custom Overlay to replace standard InfoWindow for full styling control.
     */
    class CustomInfoWindow extends google.maps.OverlayView {
        constructor() {
            super();
            this.container = document.createElement('div');
            this.container.className = 'properties-map-infowindow';
            this.container.style.position = 'absolute';
            this.container.style.cursor = 'default';
            
            // Create Close Button
            const closeBtn = document.createElement('button');
            closeBtn.type = 'button';
            closeBtn.className = 'properties-map-infowindow__close';
            closeBtn.innerHTML = '&times;';
            Object.assign(closeBtn.style, {
                position: 'absolute',
                top: '0',
                right: '0',
                border: 'none',
                background: 'transparent',
                fontSize: '24px',
                lineHeight: '1',
                cursor: 'pointer',
                padding: '0 5px',
                color: '#fff',
                zIndex: '10'
            });
            closeBtn.addEventListener('click', () => this.close());
            this.container.appendChild(closeBtn);

            // Create Content Wrapper
            this.contentWrapper = document.createElement('div');
            this.contentWrapper.className = 'properties-map-infowindow__content';
            this.container.appendChild(this.contentWrapper);
            
            // Prevent map events from firing when interacting with the window
            ['mousedown', 'touchstart', 'pointerdown', 'click', 'dblclick', 'contextmenu'].forEach(evt => {
                this.container.addEventListener(evt, (e) => e.stopPropagation());
            });
        }

        onAdd() {
            this.getPanes().floatPane.appendChild(this.container);
        }

        onRemove() {
            if (this.container.parentElement) {
                this.container.parentElement.removeChild(this.container);
            }
        }

        draw() {
            const projection = this.getProjection();
            if (!projection || !this.position) return;

            const point = projection.fromLatLngToDivPixel(this.position);
            // Offset to position window above the marker (approx 50px up)
            const offset = 50; 

            this.container.style.left = point.x + 'px';
            this.container.style.top = (point.y - offset) + 'px';
            this.container.style.transform = 'translate(-50%, -100%)'; // Center horizontally, sit above anchor
            this.container.style.zIndex = '1000';
        }

        setContent(html) {
            this.contentWrapper.innerHTML = html;
        }

        open(map, marker) {
            this.position = marker.getPosition();
            this.setMap(map);
            this.draw();
            
            // Ensure the dialog is in view immediately
            requestAnimationFrame(() => this.panToView());

            // Also check again if images load, as the dialog grows upwards and might go off-screen
            const images = this.container.querySelectorAll('img');
            images.forEach(img => {
                img.addEventListener('load', () => this.panToView());
            });
        }

        panToView() {
            const map = this.getMap();
            if (!map || !this.container) return;

            const mapDiv = map.getDiv();
            const mapRect = mapDiv.getBoundingClientRect();
            const bounds = this.container.getBoundingClientRect();
            
            const padding = 20; // Padding from edge in pixels
            let dx = 0;
            let dy = 0;

            // Check Left
            if (bounds.left < mapRect.left + padding) {
                dx = bounds.left - (mapRect.left + padding);
            }
            // Check Right
            else if (bounds.right > mapRect.right - padding) {
                dx = bounds.right - (mapRect.right - padding);
            }

            // Check Top
            if (bounds.top < mapRect.top + padding) {
                dy = bounds.top - (mapRect.top + padding);
            }
            // Check Bottom
            else if (bounds.bottom > mapRect.bottom - padding) {
                dy = bounds.bottom - (mapRect.bottom - padding);
            }

            if (dx !== 0 || dy !== 0) {
                map.panBy(dx, dy);
            }
        }

        close() {
            this.setMap(null);
            if (this.onClose) this.onClose();
        }
    }

    infoWindow = new CustomInfoWindow();
    
    // Close window when clicking on the map background
    map.addListener('click', () => {
        infoWindow.close();
    });

    // Callback to reset marker state when window closes
    infoWindow.onClose = () => {
        if (selectedMarker) {
            resetMarkerIcon(selectedMarker);
            selectedMarker = null;
        }
    };

    mapInitialized = true;
    
    updateMapMarkers();
}

function updateMapMarkers() {
    if (!map) return;

    // Close any open info window when refreshing markers
    if (infoWindow && typeof infoWindow.close === 'function') {
        infoWindow.close();
    }

    // Clear existing markers and clusterer
    if (markerCluster) {
        markerCluster.clearMarkers();
    }
    markers = [];
    selectedMarker = null;

    const bounds = new google.maps.LatLngBounds();
    let hasMarkers = false;

    // Define Icons
    const defaultIcon = {
        url: DEFAULT_MARKER_SVG,
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 12)
    };

    const selectedIcon = {
        url: SELECTED_MARKER_SVG,
        scaledSize: new google.maps.Size(46, 46),
        anchor: new google.maps.Point(23, 46)
    };

    const items = document.querySelectorAll('#archive-list-view .alm-listing .item');
    const positionCounts = {}; // Track coordinates to handle duplicates
    
    items.forEach(item => {
        let lat = parseFloat(item.dataset.lat);
        let lng = parseFloat(item.dataset.lng);
        
        const titleEl = item.querySelector('h3, h4');
        const title = titleEl ? titleEl.textContent.trim() : '';
        
        const linkEl = item.querySelector('a');
        const link = linkEl ? linkEl.getAttribute('href') : '';
        
        const imgEl = item.querySelector('.property-listing__image img,.project-listing__image img');
        const img = imgEl ? imgEl.getAttribute('src') : '';
        
        const contentPs = item.querySelectorAll('.content p');
        const address = contentPs.length > 0 ? contentPs[contentPs.length - 1].textContent : '';

        if (!isNaN(lat) && !isNaN(lng)) {
            // Handle duplicate coordinates by applying a small offset (jitter)
            const coordKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
            if (!positionCounts[coordKey]) {
                positionCounts[coordKey] = 0;
            }
            positionCounts[coordKey]++;

            if (positionCounts[coordKey] > 1) {
                // Apply spiral offset to separate overlapping markers
                // 0.00008 degrees is approx 8-9 meters
                const count = positionCounts[coordKey] - 1;
                const angle = count * 2.4; // Golden angle in radians (~137.5 degrees)
                const radius = 0.00008 * Math.sqrt(count); 
                
                lat += radius * Math.cos(angle);
                lng += radius * Math.sin(angle);
            }

            const marker = new google.maps.Marker({
                position: { lat, lng },
                // map: map, // Removed map property so MarkerClusterer handles it
                title: title,
                icon: defaultIcon
            });

            marker.addListener('click', () => {
                // Handle Icon State
                if (selectedMarker && selectedMarker !== marker) {
                    resetMarkerIcon(selectedMarker);
                }
                
                marker.setIcon(selectedIcon);
                marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
                selectedMarker = marker;

                // Handle Info Window
                let content = '<div style="padding:0px; width:280px;"><a href="${link}" style="display:block;width:100%;aspect-ratio:1.6;background-color:#efeee9;">';
                if (img) {
                    content += `<img src="${img}" style="position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;object-position:center;display:block;">`;
                }
                content += `</a><div class="properties-map-infowindow__details"><strong style="display:block;"><a href="${link}">${title}</a></strong>`;
                if (address) {
                    content += `<div>${address}</div>`;
                }
                content += '</div></div>';
                
                infoWindow.setContent(content);
                infoWindow.open(map, marker);
            });

            markers.push(marker);
            bounds.extend(marker.position);
            hasMarkers = true;
        }
    });

    // Initialize or Update Clusterer
    if (hasMarkers) {
        map.fitBounds(bounds);
        
        // Custom Renderer for Clustering
        const renderer = {
            render: ({ count, position }) => {
                return new google.maps.Marker({
                    label: { 
                        text: String(count), 
                        color: "white", 
                        fontSize: "12px",
                        fontWeight: "bold"
                    },
                    position,
                    icon: defaultIcon,
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                });
            },
        };

        markerCluster = new MarkerClusterer({ map, markers, renderer });
    }
}

function resetMarkerIcon(marker) {
    const defaultIcon = {
        url: DEFAULT_MARKER_SVG,
        scaledSize: new google.maps.Size(21, 21),
        anchor: new google.maps.Point(10.5, 21)
    };
    marker.setIcon(defaultIcon);
    marker.setZIndex(null);
}


function onAlmComplete(e) {
    const alm = e.detail;
    
    if (mapInitialized) {
        updateMapMarkers();
    }

    // Ensure map-display querystring persists after ALM updates the URL
    const toggle = document.getElementById('map-view-toggle');
    if (toggle && toggle.checked) {
        const url = new URL(window.location);
        if (url.searchParams.get('map-display') !== '1') {
            url.searchParams.set('map-display', '1');
            window.history.replaceState(window.history.state, '', url);
        }
    }
}

function setupEventListeners() {
    const toggle = document.getElementById('map-view-toggle');
    const listView = document.getElementById('archive-list-view');
    const mapView = document.getElementById('archive-map-view');
    const mapEl = document.getElementById('properties-map');

    if (toggle) {
        toggle.addEventListener('change', (e) => {
            const lenis = getLenisInstance();
            const filterWrap = document.querySelector('.filter-wrap');
            const header = document.getElementById('header');
            const wpAdminBar = document.getElementById('wpadminbar');

            if (e.target.checked) {
                if (listView) listView.classList.remove('active');
                if (mapView) mapView.classList.add('active');
                
                if (!mapInitialized) {
                    loadAndInitMap(mapEl);
                } else if (map) {
                    google.maps.event.trigger(map, 'resize');
                    updateMapMarkers(); 
                }

                if (lenis) {
                    if (filterWrap) {
                        const rect = filterWrap.getBoundingClientRect();
                        if (rect.top > 0) {
                            let offset = 0;
                            if (header) offset += header.offsetHeight;
                            //if (filterWrap) offset += filterWrap.offsetHeight;
                            if (wpAdminBar) offset += wpAdminBar.offsetHeight;

                            lenis.scrollTo(filterWrap, {
                                offset: -offset,
                                onComplete: () => {
                                    lenis.stop();
                                }
                            });
                        } else {
                            lenis.stop();
                        }
                    } else {
                        lenis.stop();
                    }
                }

                // Update URL
                const url = new URL(window.location);
                url.searchParams.set('map-display', '1');
                window.history.replaceState(window.history.state || {}, '', url);
            } else {
                if (mapView) mapView.classList.remove('active');
                if (listView) listView.classList.add('active');

                if (lenis) lenis.start();

                // Update URL
                const url = new URL(window.location);
                url.searchParams.delete('map-display');
                window.history.replaceState(window.history.state || {}, '', url);
            }
        });
    }

    // Hook into Ajax Load More via custom event dispatched by alm-features.js
    window.addEventListener('alm:complete', onAlmComplete);
}